<template>
  <div class="group-chat" :class="viewClass" :style="detachedStyle">
    <!-- Header bar — always visible -->
    <div class="group-chat__header" @pointerdown="startDetachedDrag">
      <span
        class="group-chat__title"
        :class="{ 'group-chat__title--flash': newMessageFlash }"
      >
        {{ chatTitle }}
      </span>
      <q-space />
      <q-badge
        v-if="viewState === 'collapsed' && unreadCount > 0"
        color="red"
        floating
        :label="unreadCount"
        class="q-mr-sm"
      />
      <q-btn
        v-if="detached || viewState !== 'collapsed'"
        flat
        dense
        round
        :icon="detached ? 'keyboard_return' : 'open_in_new'"
        size="sm"
        :title="detached ? 'Dock chat' : 'Pop out chat'"
        @pointerdown.stop
        @click.stop="toggleDetached"
      />
      <q-btn
        v-if="viewState !== 'collapsed'"
        flat
        dense
        round
        icon="remove"
        size="sm"
        title="Minimize"
        @pointerdown.stop
        @click.stop="viewState = 'collapsed'"
      />
      <q-btn
        flat
        dense
        round
        :icon="headerIcon"
        size="sm"
        :title="
          viewState === 'collapsed'
            ? 'Open'
            : viewState === 'mini'
            ? 'Expand'
            : 'Shrink'
        "
        @pointerdown.stop
        @click.stop="cycleState"
      />
    </div>

    <!-- Viewer role notice -->
    <div v-show="viewState !== 'collapsed'" class="group-chat__banner">
      This chat is not visible to users with &ldquo;viewer&rdquo; role
    </div>

    <div v-show="viewState !== 'collapsed'" class="group-chat__conversations">
      <q-select
        v-model="activeConversationId"
        dense
        outlined
        emit-value
        map-options
        :options="conversationOptions"
        popup-content-class="group-chat__select-menu"
        class="group-chat__conversation-select"
      />
      <div class="group-chat__conversation-actions">
        <q-btn
          dense
          flat
          icon="person_add"
          title="New private chat"
          @click="toggleNewConversation"
        />
        <q-btn
          v-if="canLeaveActiveConversation"
          dense
          flat
          icon="logout"
          title="Leave conversation"
          @click="openLeaveConversationDialog"
        />
      </div>
      <div v-if="showNewConversation" class="group-chat__new-chat">
        <q-select
          v-model="selectedParticipantIds"
          dense
          outlined
          multiple
          emit-value
          map-options
          options-dense
          placeholder="Select people"
          :display-value="selectedParticipantDisplay"
          :options="participantOptions"
          popup-content-class="group-chat__select-menu group-chat__select-menu--participants"
          class="group-chat__participant-select"
        />
        <q-btn
          dense
          color="primary"
          label="Start"
          :disable="selectedParticipantIds.length === 0"
          @click="createPrivateConversation"
        />
      </div>
    </div>

    <!-- Messages + input — hidden when collapsed -->
    <div
      v-show="viewState !== 'collapsed'"
      ref="messageList"
      class="group-chat__messages"
      @scroll="onScroll"
    >
      <div v-if="loadingOlder" class="group-chat__loading">
        Loading older messages…
      </div>
      <div v-if="loading" class="group-chat__loading">Loading…</div>
      <div v-if="!loading && messages.length === 0" class="group-chat__empty">
        No chat history yet
      </div>
      <div
        v-for="msg in messages"
        :key="msg.id"
        class="group-chat__bubble"
        :class="{
          'group-chat__bubble--event':
            msg.messageType === GroupChatMessageType.UserLeft,
          'group-chat__bubble--own': msg.userId === currentUserId,
        }"
      >
        <template v-if="msg.messageType === GroupChatMessageType.UserLeft">
          <q-icon name="logout" size="14px" class="q-mr-xs" />
          <span>{{ msg.text }}</span>
          <span class="group-chat__event-time">{{
            formatTime(msg.timestamp)
          }}</span>
        </template>
        <template v-else>
          <div class="group-chat__meta">
            <span class="group-chat__author">{{ msg.userName }}</span>
            <span class="group-chat__time">{{
              formatTime(msg.timestamp)
            }}</span>
            <q-btn
              v-if="msg.userId === currentUserId"
              flat
              dense
              round
              icon="delete_outline"
              size="xs"
              color="grey-6"
              class="group-chat__delete"
              @click="deleteMessage(msg.id)"
            />
          </div>
          <div v-if="msg.text" class="group-chat__text">
            <span>{{ msg.text }}</span>
          </div>

          <!-- Attachment -->
          <div v-if="msg.attachment" class="group-chat__attachment">
            <template
              v-if="
                msg.attachment.contentType &&
                msg.attachment.contentType.startsWith('image/')
              "
            >
              <img
                v-show="attachmentUrls[msg.attachment.blobName]"
                :alt="msg.attachment.originalName"
                class="group-chat__attachment-image"
                style="cursor: zoom-in"
                :src="attachmentUrls[msg.attachment.blobName] || ''"
                @click="
                  openImagePreview(
                    msg.attachment.blobName,
                    msg.attachment.originalName
                  )
                "
              />
              <div
                v-if="!attachmentUrls[msg.attachment.blobName]"
                class="group-chat__attachment-image-loading"
              >
                <q-spinner size="16px" /> Loading image...
              </div>
            </template>
            <template v-else>
              <div
                class="group-chat__attachment-file"
                @click="downloadAttachment(msg.attachment.blobName)"
              >
                <q-icon name="attach_file" size="16px" class="q-mr-xs" />
                <span
                  class="group-chat__attachment-name"
                  :title="msg.attachment.originalName"
                  >{{ msg.attachment.originalName }}</span
                >
                <span class="group-chat__attachment-size">{{
                  formatFileSize(msg.attachment.sizeBytes)
                }}</span>
              </div>
            </template>
          </div>

          <!-- Reactions row -->
          <div
            v-if="getReactions(msg.id).length > 0 || true"
            class="group-chat__reactions"
          >
            <span
              v-for="r in getReactions(msg.id)"
              :key="r.emoji"
              class="group-chat__reaction-chip"
              :class="{
                'group-chat__reaction-chip--mine':
                  r.userIds.includes(currentUserId),
              }"
              :title="r.userIds.length + ' reaction(s)'"
              @click="toggleReaction(msg.id, r.emoji)"
            >
              {{ r.emoji }}
              <span class="group-chat__reaction-count">{{
                r.userIds.length
              }}</span>
            </span>
            <span
              class="group-chat__reaction-add"
              title="Add reaction"
              @click.stop="openEmojiPicker($event, msg.id)"
              >😀+</span
            >
          </div>
        </template>
      </div>

      <!-- Typing indicator -->
      <div v-if="someoneTyping" class="group-chat__typing">
        <span>Someone is typing</span>
        <span class="typing-dots">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </div>
    </div>

    <div v-show="viewState !== 'collapsed'" class="group-chat__input">
      <!-- Upload preview -->
      <div v-if="pendingAttachment" class="group-chat__upload-preview">
        <q-icon name="attach_file" size="14px" class="q-mr-xs" />
        <span class="group-chat__upload-name">{{
          pendingAttachment.originalName
        }}</span>
        <span class="group-chat__attachment-size">{{
          formatFileSize(pendingAttachment.sizeBytes)
        }}</span>
        <q-btn
          flat
          dense
          round
          icon="close"
          size="xs"
          @click="pendingAttachment = null"
        />
      </div>
      <div v-if="uploading" class="group-chat__upload-preview">
        <q-spinner size="14px" class="q-mr-xs" />
        <span>Uploading...</span>
      </div>
      <q-input
        ref="chatInput"
        v-model="draft"
        dense
        outlined
        placeholder="Type a message…"
        class="group-chat__field"
        maxlength="2000"
        :error="isOverLimit"
        :error-message="`Message exceeds ${2000} characters`"
        hide-bottom-space
        @keydown="onInputKeydown"
        @paste="onPaste"
      >
        <template #append>
          <span
            v-if="draft.length > 1800"
            class="group-chat__char-count"
            :class="{ 'group-chat__char-count--over': isOverLimit }"
            >{{ charsRemaining }}</span
          >
          <span
            class="group-chat__emoji-btn"
            title="Insert emoji"
            @click.stop="openInputEmojiPicker"
            >😀</span
          >
          <span
            class="group-chat__attach-btn"
            title="Attach file"
            @click="triggerFileInput"
          >
            <q-icon name="attach_file" size="18px" />
          </span>
          <q-btn
            flat
            dense
            round
            icon="send"
            color="primary"
            :disable="!draft.trim() || sending || isOverLimit"
            :loading="sending"
            @click="send"
          />
        </template>
      </q-input>
      <input
        ref="fileInput"
        type="file"
        style="display: none"
        accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.xlsx,.csv,.agm,.onl,.onlv,.agmv,.ofl,.zip,.text,.txt,.md,.json,.xml,.pptx"
        @change="onFileSelected"
      />
    </div>

    <!-- Image lightbox preview — teleported to body so no parent clipping applies -->
    <Teleport to="body">
      <div
        v-if="lightboxOpen"
        class="group-chat__lightbox-overlay"
        @click="lightboxOpen = false"
      >
        <div class="group-chat__lightbox" @click.stop>
          <img
            :src="lightboxSrc"
            :alt="lightboxAlt"
            class="group-chat__lightbox-img"
          />
          <div class="group-chat__lightbox-actions">
            <q-btn
              flat
              dense
              round
              icon="download"
              color="white"
              title="Download"
              @click="downloadLightboxImage"
            />
            <q-btn
              flat
              dense
              round
              icon="close"
              color="white"
              title="Close"
              @click="lightboxOpen = false"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Emoji picker popup (shared for reactions and input) -->
    <div
      v-if="emojiPickerOpen"
      class="group-chat__emoji-overlay"
      @click="emojiPickerOpen = false"
    >
      <div
        class="group-chat__emoji-picker"
        :style="{ bottom: emojiPickerBottom + 'px', right: '8px' }"
        @click.stop
      >
        <span
          v-for="e in emojiOptions"
          :key="e"
          class="group-chat__emoji-option"
          @click="onEmojiSelected(e)"
          >{{ e }}</span
        >
      </div>
    </div>

    <q-dialog v-model="leaveConfirmDialog">
      <q-card style="width: 320px; max-width: 90vw">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold">
            Leave Conversation
          </q-toolbar-title>
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="column">
            <div class="col q-pb-sm">Leave {{ leaveConversationLabel }}?</div>
          </div>
          <q-separator />
          <q-card-actions align="center">
            <q-btn v-close-popup class="btn-brand" label="No" no-caps />
            <q-btn
              v-close-popup
              class="btn-brand"
              label="Yes-Leave"
              no-caps
              @click="confirmLeaveConversation"
            />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { marked } from 'marked';
import GroupChatService, {
  ChatAttachment,
  GroupChatConversation,
  GroupChatParticipant,
} from '../services/GroupChatService';
import {
  GroupChatConversationType,
  GroupChatMessageType,
} from '../models/GroupChat';

/**
 * Shape returned by GET /chat/messages and pushed via
 * the SignalR `ReceiveChatMessage` event.
 *
 * The `userName` field is resolved server-side from the Users table
 * using the `userId` foreign key, since the user's identity comes
 * from Auth0 where the username is their email address.
 */
export interface IChatMessage {
  /** Server-assigned unique id (GroupChatMessageId) */
  id: number;
  /** Conversation id (GroupChatConversationId) */
  groupChatConversationId?: number | null;
  /** Message type lookup name. */
  messageType?: GroupChatMessageType;
  /** Auth0 user id of the author (email) */
  userId: string;
  /** Display name resolved from Users.Name */
  userName: string;
  /** Message body */
  text: string;
  /** ISO-8601 UTC timestamp */
  timestamp: string;
  /** Optional file attachment metadata */
  attachment?: ChatAttachment | null;
}

interface IMessageSegment {
  text: string;
  isMention: boolean;
}

/** Maximum message length — matches the NVARCHAR(2000) backend constraint. */
const MAX_MESSAGE_LENGTH = 2000;
const DETACHED_VIEWPORT_MARGIN = 1;
const DETACHED_COLLAPSED_HEIGHT = 44;
const DETACHED_MINI_VIEWPORT_RATIO = 0.5;

// Configure marked for safe inline rendering (no raw HTML passthrough).
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub-flavored markdown (tables, strikethrough, etc.)
});

export default defineComponent({
  name: 'GroupChat',

  emits: ['update:expanded'],

  data() {
    return {
      GroupChatConversationType,
      GroupChatMessageType,
      /** 'collapsed' = header only, 'mini' = 300px, 'expanded' = full drawer */
      viewState: 'collapsed' as 'collapsed' | 'mini' | 'expanded',
      detached: false,
      detachedPosition: { x: 24, y: 96 },
      detachedDragStart: null as null | {
        pointerId: number;
        pointerX: number;
        pointerY: number;
        panelX: number;
        panelY: number;
      },
      draft: '',
      unreadCount: 0,
      /** true when user has scrolled up — prevents auto-scroll hijacking */
      userScrolledUp: false,
      /** tracks previous message count to detect new arrivals */
      previousMessageCount: 0,
      /** prevents flooding typing signals — throttle gate */
      typingThrottled: false,
      /** drives the title text flash on incoming message */
      newMessageFlash: false,
      newMessageFlashTimer: null as ReturnType<typeof setTimeout> | null,
      /** gates animation so it doesn't fire on initial fetch */
      initialLoadComplete: false,
      /** shows the @data autocomplete popup */
      showAtMenu: false,
      /** highlighted option index in @ menu */
      atMenuActiveIndex: 0,
      showNewConversation: false,
      selectedParticipantIds: [] as string[],
      leaveConfirmDialog: false,
      /** emoji picker state */
      emojiPickerOpen: false,
      emojiPickerBottom: 60,
      emojiPickerMode: 'input' as 'input' | 'reaction',
      emojiPickerMessageId: null as number | null,
      emojiOptions: [
        '👍',
        '👎',
        '❤️',
        '😂',
        '😮',
        '🎉',
        '🔥',
        '👀',
        '✅',
        '❌',
        '💯',
        '🤔',
      ],
      /** File upload state */
      pendingAttachment: null as ChatAttachment | null,
      uploading: false,
      /** Cached SAS URLs for image attachments, keyed by blobName. */
      attachmentUrls: {} as Record<string, string>,
      /** Lightbox state */
      lightboxOpen: false,
      lightboxSrc: '',
      lightboxAlt: '',
      lightboxBlobName: '',
    };
  },

  computed: {
    currentUserId(): string {
      return this.$store.state.user?.id ?? '';
    },
    messages(): IChatMessage[] {
      return this.$store.state.groupChat.messages;
    },
    conversations(): GroupChatConversation[] {
      return this.$store.state.groupChat.conversations;
    },
    unreadConversationIds(): number[] {
      return this.$store.state.groupChat.unreadConversationIds;
    },
    participantCandidates(): GroupChatParticipant[] {
      return this.$store.state.groupChat.participantCandidates;
    },
    activeConversationId: {
      get(): number | null {
        return this.$store.state.groupChat.activeConversationId;
      },
      set(value: number | null) {
        if (value != null) {
          this.$store.dispatch('setActiveChatConversation', value).then(() => {
            this.previousMessageCount = this.messages.length;
            this.$nextTick(() => this.scrollToBottom());
          });
        }
      },
    },
    loading(): boolean {
      return this.$store.state.groupChat.loading;
    },
    loadingOlder(): boolean {
      return this.$store.state.groupChat.loadingOlder;
    },
    sending(): boolean {
      return this.$store.state.groupChat.sending;
    },
    hasMoreMessages(): boolean {
      return this.$store.state.groupChat.hasMoreMessages;
    },
    someoneTyping(): boolean {
      return this.$store.state.groupChat.someoneTyping;
    },
    dataQueryPending(): boolean {
      return this.$store.state.groupChat.dataQueryPending;
    },
    groupName(): string {
      return this.$store.state.groupChat.groupName;
    },
    chatTitle(): string {
      const name = this.groupName;
      if (!name) return 'Chat';
      const truncated = name.length > 25 ? name.substring(0, 25) + '…' : name;
      return `${truncated} Chat`;
    },
    conversationOptions(): Array<{ label: string; value: number }> {
      return this.conversations.map((conversation) => ({
        label: this.unreadConversationIds.includes(
          conversation.groupChatConversationId
        )
          ? `${this.getConversationLabel(conversation)} (new)`
          : this.getConversationLabel(conversation),
        value: conversation.groupChatConversationId,
      }));
    },
    activeConversation(): GroupChatConversation | null {
      return (
        this.conversations.find(
          (conversation) =>
            conversation.groupChatConversationId === this.activeConversationId
        ) ?? null
      );
    },
    canLeaveActiveConversation(): boolean {
      return (
        !!this.activeConversation &&
        this.activeConversation.groupChatConversationType !==
          GroupChatConversationType.PublicGroup
      );
    },
    leaveConversationLabel(): string {
      return this.activeConversation
        ? this.getConversationLabel(this.activeConversation)
        : 'this conversation';
    },
    participantOptions(): Array<{ label: string; value: string }> {
      return this.participantCandidates.map((participant) => ({
        label: participant.userName,
        value: participant.userId,
      }));
    },
    selectedParticipantDisplay(): string {
      if (this.selectedParticipantIds.length === 0) {
        return '';
      }

      const selectedNames = this.participantOptions
        .filter((option) => this.selectedParticipantIds.includes(option.value))
        .map((option) => option.label);

      if (selectedNames.length === 1) {
        return selectedNames[0];
      }

      return `${selectedNames.length} selected`;
    },
    charsRemaining(): number {
      return MAX_MESSAGE_LENGTH - this.draft.length;
    },
    isOverLimit(): boolean {
      return this.draft.length > MAX_MESSAGE_LENGTH;
    },
    expanded(): boolean {
      return this.viewState === 'expanded';
    },
    viewClass(): Record<string, boolean> {
      return {
        'group-chat--collapsed': this.viewState === 'collapsed',
        'group-chat--mini': this.viewState === 'mini',
        'group-chat--expanded': this.viewState === 'expanded',
        'group-chat--detached': this.detached,
        'group-chat--dragging': !!this.detachedDragStart,
      };
    },
    detachedStyle(): Record<string, string> {
      if (!this.detached) {
        return {};
      }

      const margin = DETACHED_VIEWPORT_MARGIN;
      const preferredSize = this.getDetachedPreferredSize();
      const displayPosition = this.clampDetachedPosition(
        this.detachedPosition.x,
        this.detachedPosition.y,
        preferredSize.width,
        preferredSize.height
      );
      const availableHeight = Math.max(
        42,
        window.innerHeight - displayPosition.y - margin
      );
      const style: Record<string, string> = {
        left: `${displayPosition.x}px`,
        width: `${preferredSize.width}px`,
      };

      if (this.viewState === 'collapsed') {
        style.bottom = `${margin}px`;
        style.maxHeight = `${availableHeight}px`;
        style.height = `${preferredSize.height}px`;
      } else if (this.viewState === 'mini') {
        style.bottom = `${margin}px`;
        style.height = `${preferredSize.height}px`;
      } else {
        style.top = `${displayPosition.y}px`;
        style.bottom = `${margin}px`;
      }

      return style;
    },
    headerIcon(): string {
      switch (this.viewState) {
        case 'collapsed':
          return 'expand_less';
        case 'mini':
          return 'open_in_full';
        case 'expanded':
          return 'close_fullscreen';
        default:
          return 'expand_less';
      }
    },
  },

  watch: {
    viewState(val: string) {
      if (val === 'expanded') {
        this.unreadCount = 0;
        this.$nextTick(() => this.scrollToBottom());
      } else if (val === 'mini') {
        this.unreadCount = 0;
        this.$nextTick(() => this.scrollToBottom());
      }
      if (this.detached) {
        this.$nextTick(() => this.reclampDetachedPanel());
      }
      this.$emit('update:expanded', val === 'expanded' && !this.detached);
    },
    detached(val: boolean) {
      if (val) {
        this.$emit('update:expanded', false);
        this.$nextTick(() => this.reclampDetachedPanel());
      } else {
        this.$emit('update:expanded', this.viewState === 'expanded');
      }
    },
    // React to new messages arriving via the store (from SignalR or send).
    messages(newMessages: IChatMessage[]) {
      if (newMessages.length > this.previousMessageCount) {
        // Only flash for incoming SignalR messages, not initial fetch or own sends.
        const newest = newMessages[newMessages.length - 1];
        if (
          this.initialLoadComplete &&
          newest &&
          newest.userId !== this.currentUserId
        ) {
          this.triggerNewMessageFlash();
        }
        if (this.viewState !== 'collapsed' && !this.userScrolledUp) {
          this.$nextTick(() => this.scrollToBottom());
        } else if (this.viewState === 'collapsed') {
          if (this.initialLoadComplete) {
            this.unreadCount += newMessages.length - this.previousMessageCount;
          }
        }
      }
      this.previousMessageCount = newMessages.length;
      this.loadImageAttachmentUrls(newMessages);
    },
    unreadConversationIds(newIds: number[], oldIds: number[]) {
      const addedCount = newIds.length - oldIds.length;
      if (!this.initialLoadComplete || addedCount <= 0) return;

      this.triggerNewMessageFlash();
      if (this.viewState === 'collapsed') {
        this.unreadCount += addedCount;
      }
    },
    someoneTyping(val: boolean) {
      if (val && this.viewState !== 'collapsed' && !this.userScrolledUp) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    dataQueryPending(val: boolean) {
      if (val && this.viewState !== 'collapsed' && !this.userScrolledUp) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    draft(val: string) {
      if (val.length > 0 && !this.typingThrottled) {
        this.typingThrottled = true;
        this.$store.dispatch('sendChatTyping');
        setTimeout(() => {
          this.typingThrottled = false;
        }, 2000);
      }
    },
  },

  mounted() {
    this.$store
      .dispatch('fetchChatConversations')
      .then(() => this.$store.dispatch('fetchChatMessages'))
      .then(() => {
        this.previousMessageCount = this.messages.length;
        this.initialLoadComplete = true;
        this.$nextTick(() => this.scrollToBottom());
      });
    window.addEventListener('keydown', this.onWindowKeydown);
    window.addEventListener('resize', this.reclampDetachedPanel);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onWindowKeydown);
    window.removeEventListener('resize', this.reclampDetachedPanel);
    window.removeEventListener('pointermove', this.onDetachedDragMove);
    window.removeEventListener('pointerup', this.endDetachedDrag);
    window.removeEventListener('pointercancel', this.endDetachedDrag);
  },

  methods: {
    // ── UI handlers ────────────────────────────────────────────
    cycleState() {
      if (this.viewState === 'collapsed') {
        this.viewState = 'mini';
      } else if (this.viewState === 'mini') {
        this.viewState = 'expanded';
      } else {
        this.viewState = 'mini';
      }
    },

    toggleDetached() {
      if (this.detached) {
        this.detached = false;
        this.detachedDragStart = null;
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      const preferredSize = this.getDetachedPreferredSize();
      this.detachedPosition = this.clampDetachedPosition(
        rect?.left ?? 24,
        rect?.top ?? 96,
        preferredSize.width,
        preferredSize.height,
        this.viewState !== 'expanded'
      );
      this.detached = true;
      this.$nextTick(() => this.scrollToBottom());
    },

    getDetachedPreferredSize(): { width: number; height: number } {
      const margin = DETACHED_VIEWPORT_MARGIN;
      const maxWidth = window.innerWidth - margin * 2;
      const maxHeight = window.innerHeight - margin * 2;
      const width = Math.min(380, maxWidth);

      if (this.viewState === 'collapsed') {
        return { width, height: DETACHED_COLLAPSED_HEIGHT };
      }

      return {
        width,
        height:
          this.viewState === 'expanded'
            ? maxHeight
            : Math.min(
                Math.round(window.innerHeight * DETACHED_MINI_VIEWPORT_RATIO),
                maxHeight
              ),
      };
    },

    reclampDetachedPanel() {
      if (!this.detached) {
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      const preferredSize = this.getDetachedPreferredSize();
      this.detachedPosition = this.clampDetachedPosition(
        this.detachedPosition.x,
        this.detachedPosition.y,
        preferredSize.width,
        preferredSize.height,
        this.viewState !== 'expanded'
      );
    },

    startDetachedDrag(event: PointerEvent) {
      if (!this.detached || event.button !== 0) {
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      this.detachedDragStart = {
        pointerId: event.pointerId,
        pointerX: event.clientX,
        pointerY: event.clientY,
        panelX: rect.left,
        panelY: rect.top,
      };
      window.addEventListener('pointermove', this.onDetachedDragMove);
      window.addEventListener('pointerup', this.endDetachedDrag);
      window.addEventListener('pointercancel', this.endDetachedDrag);
      event.preventDefault();
    },

    onDetachedDragMove(event: PointerEvent) {
      const dragStart = this.detachedDragStart;
      if (!dragStart || event.pointerId !== dragStart.pointerId) {
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      const preferredSize = this.getDetachedPreferredSize();
      const width = preferredSize.width;
      const height = Math.max(preferredSize.height, rect?.height ?? 0);
      this.detachedPosition = this.clampDetachedPosition(
        dragStart.panelX + event.clientX - dragStart.pointerX,
        dragStart.panelY + event.clientY - dragStart.pointerY,
        width,
        height,
        this.viewState !== 'expanded'
      );
    },

    endDetachedDrag(event: PointerEvent) {
      if (
        this.detachedDragStart &&
        event.pointerId !== this.detachedDragStart.pointerId
      ) {
        return;
      }

      this.detachedDragStart = null;
      window.removeEventListener('pointermove', this.onDetachedDragMove);
      window.removeEventListener('pointerup', this.endDetachedDrag);
      window.removeEventListener('pointercancel', this.endDetachedDrag);
    },

    clampDetachedPosition(
      x: number,
      y: number,
      width: number,
      height: number,
      snapToBottom = false
    ) {
      const margin = DETACHED_VIEWPORT_MARGIN;
      const maxX = Math.max(margin, window.innerWidth - width - margin);
      const maxY = Math.max(margin, window.innerHeight - height - margin);
      return {
        x: Math.min(Math.max(x, margin), maxX),
        y: snapToBottom ? maxY : Math.min(Math.max(y, margin), maxY),
      };
    },

    getConversationLabel(conversation: GroupChatConversation): string {
      if (
        conversation.groupChatConversationType ===
        GroupChatConversationType.PublicGroup
      ) {
        return 'Public Group';
      }
      if (conversation.title) {
        return conversation.title;
      }

      const names = conversation.participants
        .filter((participant) => participant.userId !== this.currentUserId)
        .map((participant) => participant.userName)
        .filter(Boolean);
      if (names.length === 0) {
        return 'Private Chat';
      }
      if (names.length === 1) {
        return names[0];
      }

      return names.join(', ');
    },

    toggleNewConversation() {
      this.showNewConversation = !this.showNewConversation;
      if (this.showNewConversation) {
        this.$store.dispatch('fetchChatParticipantCandidates');
      }
    },

    createPrivateConversation() {
      if (this.selectedParticipantIds.length === 0) {
        return;
      }

      this.$store
        .dispatch('createChatConversation', {
          participantUserIds: this.selectedParticipantIds,
        })
        .then(() => {
          this.selectedParticipantIds = [];
          this.showNewConversation = false;
          this.previousMessageCount = this.messages.length;
        });
    },

    openLeaveConversationDialog() {
      if (!this.activeConversation || !this.canLeaveActiveConversation) {
        return;
      }

      this.leaveConfirmDialog = true;
    },

    confirmLeaveConversation() {
      if (!this.activeConversation || !this.canLeaveActiveConversation) {
        return;
      }

      this.$store.dispatch(
        'leaveChatConversation',
        this.activeConversation.groupChatConversationId
      );
    },

    formatTime(iso: string): string {
      // API timestamps without an offset are UTC, not browser-local time.
      const utcIso = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`;
      const d = new Date(utcIso);
      return (
        d.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'UTC',
        }) + ' UTC'
      );
    },

    scrollToBottom() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    onScroll() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      if (!el) return;

      // Trigger pagination when scrolled near the top.
      if (el.scrollTop < 50 && this.hasMoreMessages && !this.loadingOlder) {
        this.loadOlderMessages();
      }

      // Consider "at bottom" if within 40px of the end.
      this.userScrolledUp =
        el.scrollTop + el.clientHeight < el.scrollHeight - 40;
    },

    loadOlderMessages() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      const previousScrollHeight = el?.scrollHeight ?? 0;

      this.$store.dispatch('loadOlderChatMessages').then(() => {
        // Preserve scroll position so older messages don't push the view down.
        this.$nextTick(() => {
          if (el) {
            el.scrollTop = el.scrollHeight - previousScrollHeight;
          }
        });
      });
    },

    async send() {
      const text = this.draft.trim();
      if ((!text && !this.pendingAttachment) || this.isOverLimit) return;

      const attachment = this.pendingAttachment;
      this.draft = '';
      this.pendingAttachment = null;

      this.$store.dispatch('sendChatMessage', {
        text: text,
        attachment: attachment ? JSON.stringify(attachment) : null,
      });
      this.$nextTick(() => this.scrollToBottom());
    },

    deleteMessage(id: number) {
      this.$store.dispatch('deleteChatMessage', id);
    },

    onInputKeydown(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.send();
      }
    },

    getInputElement(): HTMLInputElement | null {
      const inputRef = this.$refs.chatInput as any;
      const root = inputRef?.$el as HTMLElement | undefined;
      return (root?.querySelector('input') as HTMLInputElement | null) ?? null;
    },

    getReactions(messageId: number) {
      return this.$store.state.groupChat.reactions[messageId] || [];
    },

    toggleReaction(messageId: number, emoji: string) {
      this.$store.dispatch('toggleReaction', { messageId, emoji });
    },

    openEmojiPicker(event: MouseEvent, messageId: number) {
      this.emojiPickerMode = 'reaction';
      this.emojiPickerMessageId = messageId;

      const chatEl = this.$el as HTMLElement;
      const chatRect = chatEl.getBoundingClientRect();
      const bottomOffset = chatRect.bottom - event.clientY;

      // Clamp so the picker (~120px tall) stays within the chat bounds.
      const pickerHeight = 120;
      this.emojiPickerBottom = Math.max(
        8,
        Math.min(bottomOffset, chatRect.height - pickerHeight - 8)
      );

      this.emojiPickerOpen = true;
    },

    openInputEmojiPicker() {
      this.emojiPickerMode = 'input';
      this.emojiPickerMessageId = null;
      this.emojiPickerBottom = 50;
      this.emojiPickerOpen = true;
    },

    onEmojiSelected(emoji: string) {
      if (
        this.emojiPickerMode === 'reaction' &&
        this.emojiPickerMessageId != null
      ) {
        const existing = this.getReactions(this.emojiPickerMessageId).find(
          (r: any) => r.emoji === emoji
        );
        const alreadyReacted = existing?.userIds?.includes(this.currentUserId);

        // Picker is add-only to avoid accidental removals.
        // Users can still toggle off by clicking the reaction chip itself.
        if (!alreadyReacted) {
          this.toggleReaction(this.emojiPickerMessageId, emoji);
        }
      } else {
        this.draft += emoji;
      }
      this.emojiPickerOpen = false;
    },

    triggerFileInput() {
      (this.$refs.fileInput as HTMLInputElement)?.click();
    },

    async onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (!file) return;
      input.value = ''; // Reset so the same file can be selected again

      await this.uploadFile(file);
    },

    async onPaste(event: ClipboardEvent) {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (!file) break;
          await this.uploadFile(file);
          break;
        }
      }
    },

    async uploadFile(file: File) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File exceeds 10 MB limit.');
        return;
      }

      this.uploading = true;
      try {
        this.pendingAttachment = await GroupChatService.uploadFile(file);
      } catch (e) {
        console.error('Failed to upload file:', e);
        alert('Failed to upload file. Please try again.');
      } finally {
        this.uploading = false;
      }
    },

    openImagePreview(blobName: string, originalName: string) {
      const src = this.attachmentUrls[blobName];
      if (!src) return;
      this.lightboxSrc = src;
      this.lightboxAlt = originalName;
      this.lightboxBlobName = blobName;
      this.lightboxOpen = true;
    },

    onWindowKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape' && this.lightboxOpen) {
        this.lightboxOpen = false;
      }
    },

    async downloadAttachment(blobName: string) {
      try {
        const url = await GroupChatService.getAttachmentUrl(blobName);
        window.open(url, '_blank');
      } catch (e) {
        console.error('Failed to get attachment URL:', e);
      }
    },

    async downloadLightboxImage() {
      try {
        const response = await fetch(this.lightboxSrc);
        const blob = await response.blob();
        const ext = this.lightboxAlt.includes('.')
          ? this.lightboxAlt.split('.').pop()
          : blob.type.split('/')[1] || 'png';
        const timestamp = new Date()
          .toISOString()
          .replace(/[:.]/g, '-')
          .replace('T', '_')
          .slice(0, 19);
        const filename = `${timestamp}-propipe-chat.${ext}`;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        console.error('Failed to download image:', e);
      }
    },

    formatFileSize(bytes: number): string {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async loadImageAttachmentUrls(msgs: IChatMessage[]) {
      for (const msg of msgs) {
        if (
          msg.attachment &&
          msg.attachment.contentType?.startsWith('image/') &&
          msg.attachment.blobName &&
          !this.attachmentUrls[msg.attachment.blobName]
        ) {
          try {
            const url = await GroupChatService.getAttachmentUrl(
              msg.attachment.blobName
            );
            this.attachmentUrls = {
              ...this.attachmentUrls,
              [msg.attachment.blobName]: url,
            };
            this.$nextTick(() => {
              if (!this.userScrolledUp) this.scrollToBottom();
            });
          } catch (e) {
            console.error(
              'Failed to load image URL for',
              msg.attachment.blobName,
              e
            );
          }
        }
      }
    },

    triggerNewMessageFlash() {
      if (this.newMessageFlashTimer) clearTimeout(this.newMessageFlashTimer);
      this.newMessageFlash = false;
      // Force a reflow so the animation restarts if already playing.
      void (this.$el as HTMLElement)?.offsetWidth;
      this.newMessageFlash = true;
      this.newMessageFlashTimer = setTimeout(() => {
        this.newMessageFlash = false;
        this.newMessageFlashTimer = null;
      }, 2000);
    },
  },
});
</script>

<style scoped>
.group-chat {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ddd;
  background: #fff;
  margin-top: auto;
  flex-shrink: 0;
  box-sizing: border-box;
}

.group-chat--detached {
  position: fixed;
  z-index: 4000;
  margin-top: 0;
  box-sizing: border-box;
  border: 1px solid #c8ccd6;
  border-radius: 8px;
  box-shadow: 0 16px 42px rgba(25, 35, 65, 0.28);
  overflow: hidden;
}

.group-chat--detached.group-chat--mini {
  height: auto;
}

.group-chat--detached.group-chat--expanded {
  flex: none;
}

.group-chat--detached.group-chat--collapsed {
  height: auto;
}

.group-chat--dragging {
  user-select: none;
}

.group-chat__title--flash {
  animation: chat-title-flash 2s ease-out forwards;
}

@keyframes chat-title-flash {
  0% {
    color: #ff0080;
    text-shadow: 0 0 6px rgba(255, 0, 128, 0.7);
  }
  16% {
    color: #ff6600;
    text-shadow: 0 0 6px rgba(255, 102, 0, 0.7);
  }
  33% {
    color: #f5d800;
    text-shadow: 0 0 6px rgba(245, 216, 0, 0.7);
  }
  50% {
    color: #00cc55;
    text-shadow: 0 0 6px rgba(0, 204, 85, 0.7);
  }
  66% {
    color: #0099ff;
    text-shadow: 0 0 6px rgba(0, 153, 255, 0.7);
  }
  83% {
    color: #9900ff;
    text-shadow: 0 0 6px rgba(153, 0, 255, 0.7);
  }
  100% {
    color: #fff;
    text-shadow: none;
  }
}

/* Collapsed — header bar only */
.group-chat--collapsed {
  height: auto;
}

/* Mini — fixed compact height */
.group-chat--mini {
  height: 50vh;
}

/* Expanded — take all remaining space in the drawer */
.group-chat--expanded {
  height: auto;
  flex: 1 1 0;
  min-height: 0;
}

.group-chat__header {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #24337a;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
  min-height: 42px;
  box-sizing: border-box;
}

.group-chat--detached .group-chat__header {
  cursor: move;
}

.group-chat__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* ── Message list ─────────────────────────────────── */
.group-chat__messages {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f5f5f5;
  -webkit-overflow-scrolling: touch;
}

/* ── Bubble ───────────────────────────────────────── */
.group-chat__bubble {
  max-width: 85%;
  padding: 6px 10px;
  border-radius: 10px;
  background: #e3e8f0;
  align-self: flex-start;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.group-chat__bubble--own {
  background: #d1e7dd;
  align-self: flex-end;
}

.group-chat__bubble--event {
  max-width: 92%;
  align-self: center;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 9px;
  border-radius: 999px;
  background: #eef1f5;
  color: #5f6673;
  font-size: 11px;
}

.group-chat__event-time {
  color: #888;
  white-space: nowrap;
}

.group-chat__meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
}

.group-chat__author {
  font-size: 11px;
  font-weight: 600;
  color: #24337a;
}

.group-chat__bubble--own .group-chat__author {
  color: #0f5132;
}

.group-chat__time {
  font-size: 10px;
  color: #888;
}

.group-chat__text {
  font-size: 13px;
  line-height: 1.35;
  color: #222;
}

/* ── Input area ───────────────────────────────────── */
.group-chat__input {
  position: relative;
  padding: 6px 8px;
  border-top: 1px solid #ddd;
  background: #fff;
  flex-shrink: 0;
}

.group-chat__field {
  font-size: 13px;
}

.group-chat__loading {
  text-align: center;
  font-size: 11px;
  color: #999;
  padding: 6px 0;
}

.group-chat__delete {
  opacity: 0;
  transition: opacity 0.15s;
  margin-left: auto;
}

.group-chat__bubble:hover .group-chat__delete {
  opacity: 1;
}

.group-chat__char-count {
  font-size: 11px;
  color: #999;
  margin-right: 4px;
  line-height: 1;
}

.group-chat__char-count--over {
  color: #c10015;
  font-weight: 600;
}

/* ── Viewer notice banner ─────────────────────────── */
.group-chat__banner {
  padding: 6px 12px;
  background: #ff9800;
  color: #fff;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  flex-shrink: 0;
}

.group-chat__conversations {
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
  background: #fff;
  flex-shrink: 0;
}

.group-chat__conversation-select {
  font-size: 12px;
}

.group-chat__conversation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
}

.group-chat__new-chat {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.group-chat__new-chat .q-btn {
  flex: 0 0 auto;
}

.group-chat__participant-select {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
}

.group-chat__participant-select ::v-deep(.q-field__native),
.group-chat__participant-select ::v-deep(.q-field__input) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Empty state ──────────────────────────────────── */
.group-chat__empty {
  text-align: center;
  font-size: 12px;
  color: #999;
  padding: 24px 10px;
  font-style: italic;
}

/* ── Typing indicator ─────────────────────────────── */
.group-chat__typing {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.typing-dots {
  display: inline-flex;
  gap: 3px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #888;
  animation: typing-bounce 1.4s ease-in-out infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
}

/* ── Reactions ────────────────────────────────────── */

/* ── Attachments ──────────────────────────────────── */
.group-chat__attachment {
  margin-top: 4px;
}

.group-chat__attachment-image {
  max-width: 280px;
  max-height: 200px;
  border-radius: 6px;
  display: block;
}

.group-chat__attachment-image-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  font-size: 12px;
  color: #888;
}

.group-chat__attachment-file {
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  color: #1976d2;
}

.group-chat__attachment-file:hover {
  background: rgba(0, 0, 0, 0.1);
}

.group-chat__attachment-size {
  flex: 0 0 auto;
  color: #888;
  font-size: 11px;
}

.group-chat__attachment-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-chat__upload-preview {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #e3f2fd;
  border-radius: 6px;
  font-size: 12px;
  margin-bottom: 4px;
}

.group-chat__upload-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.group-chat__attach-btn {
  cursor: pointer;
  opacity: 0.5;
  margin-right: 4px;
  transition: opacity 0.15s;
}

.group-chat__attach-btn:hover {
  opacity: 1;
}

/* ── Reactions ────────────────────────────────────── */
.group-chat__reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.group-chat__reaction-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.06);
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.group-chat__reaction-chip:hover {
  background: rgba(0, 0, 0, 0.12);
}

.group-chat__reaction-chip--mine {
  background: rgba(25, 118, 210, 0.15);
  outline: 1px solid rgba(25, 118, 210, 0.4);
}

.group-chat__reaction-count {
  font-size: 11px;
  color: #666;
  font-weight: 600;
}

.group-chat__reaction-add {
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 10px;
  font-size: 12px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  background: rgba(0, 0, 0, 0.04);
  user-select: none;
}

.group-chat__bubble:hover .group-chat__reaction-add {
  opacity: 1;
}

/* ── Emoji picker ─────────────────────────────────── */
.group-chat__emoji-btn {
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  margin-right: 4px;
  user-select: none;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.group-chat__emoji-btn:hover {
  opacity: 1;
}

/* ── Image lightbox — styles intentionally in global block (teleported to body) ── */

.group-chat__emoji-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
}

.group-chat__emoji-picker {
  position: absolute;
  right: 8px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 2px;
  width: 210px;
}

.group-chat__emoji-option {
  font-size: 20px;
  text-align: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  user-select: none;
  transition: background 0.1s;
}

.group-chat__emoji-option:hover {
  background: #f0f0f0;
}
</style>

<style>
/* ── GroupChat select menus (global — teleported to body) ── */
.group-chat__select-menu {
  width: 280px !important;
  max-width: calc(100vw - 24px) !important;
  max-height: 240px !important;
  overflow-y: auto;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.group-chat__select-menu--participants {
  width: 240px !important;
}

.group-chat__select-menu .q-item {
  min-height: 34px;
  padding: 6px 10px;
  font-size: 13px;
}

.group-chat__select-menu .q-item__section,
.group-chat__select-menu .q-item__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── GroupChat image lightbox (global — teleported to body) ── */
.group-chat__lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.group-chat__lightbox {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90vw;
  max-height: 90vh;
}

.group-chat__lightbox-img {
  max-width: 90vw;
  max-height: calc(90vh - 48px);
  object-fit: contain;
  border-radius: 6px;
  display: block;
}

.group-chat__lightbox-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
}

.group-chat__lightbox-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 4px;
}
</style>
